import { Stack, Text, Divider, useDisclosure, HStack, Icon } from '@chakra-ui/react';
import { memo, Suspense } from 'react';
import { some } from 'lodash';

import { useMeetingManagementActions } from '@util/hooks';
import { useUserStore } from '@store/app.store';
import { lazyWithRetry } from '@util/helpers';
import ForaBanner from '@components/general/fora-banner.component';
import MeetingPageSectionNavigation from '@pages/meeting/section-navigation.component';
import VideoThumbnail from './video-thumbnail.component';
import MeetingAttendees from '@components/meeting/meeting-attendees.component';
import MeetingOrganizations from '@components/meeting/meeting-organizations.component';
import DocumentDownloadButton from '@components/general/card/document-download-button.component';
import SummarizeAudioVideo from '@components/layout/summarize/summarize-audio-video.component';
import { UploadIcon } from '@assets/icons';
import MeetingAttachments from '@components/meeting/meeting-attachments.component';
import LabelsBar from '@components/general/labels/labels-bar.component';

const Chat = lazyWithRetry(() => import('@components/meeting/meeting-chat.component'));

function MeetingPageRightRail({
  sections,
  attendees,
  organizations,
  meeting,
  bot,
  thumbnailQueryResult,
  askEnabled = true,
  isLoading,
  card = null
}) {
  const user = useUserStore((state) => state?.user);
  const anySectionsEnabled = some(sections, { enabled: true });
  const isGuest = user?.isGuest;
  const { showViewVideoButton, showUploadVideoButton } = useMeetingManagementActions(meeting, bot);
  const showDownloadButton = card && card.sub_source === 'Document_Upload';
  const uploadVideoDisclosure = useDisclosure();

  return (
    <Stack
      gap={11}
      p={11}
      mb={16}
    >
      (showViewVideoButton || anySectionsEnabled || showDownloadButton || showUploadVideoButton) &&
      (
      <Stack
        gap={8}
        data-testid='overview-stack'
      >
        {showDownloadButton && (
          <Stack
            gap={2}
            align='flex-start'
          >
            <Text variant='content-section-header'>Original File</Text>
            <DocumentDownloadButton card={card} />
          </Stack>
        )}
        {showViewVideoButton && (
          <VideoThumbnail
            thumbnailQueryResult={thumbnailQueryResult}
            meeting={meeting}
            bot={bot}
          />
        )}
        {showUploadVideoButton && !card && (
          <HStack
            layerStyle='section-card'
            p={4}
            gap={4}
            w='full'
            onClick={uploadVideoDisclosure.onOpen}
            cursor='pointer'
            role='button'
            data-testid='upload-meeting-video-button'
          >
            <Icon
              as={UploadIcon}
              color='brandPrimary.primary'
              boxSize={7}
            />
            <Text variant=''>Have a video for this meeting? Upload it here.</Text>
          </HStack>
        )}
        <MeetingAttachments meeting={meeting} />
        {!isGuest && (
          <Stack
            gap={2}
            data-testid='labels-section'
          >
            <Text variant='content-section-header'>Labels</Text>
            <LabelsBar
              meeting={meeting}
              card={card}
              showIconForNoLabels
            />
          </Stack>
        )}
        {anySectionsEnabled && (
          <>
            <Text variant='content-section-header'>Table of Contents</Text>
            <MeetingPageSectionNavigation sections={sections} />
          </>
        )}
      </Stack>
      ){attendees && <MeetingAttendees attendees={attendees} />}
      {organizations && (
        <MeetingOrganizations
          organizations={organizations}
          meetingId={meeting?.id}
        />
      )}
      {isGuest && (
        <>
          <Divider />
          <ForaBanner />
        </>
      )}
      {!isGuest && askEnabled && !isLoading && (
        <Suspense fallback={null}>
          <Chat meeting={meeting} />
        </Suspense>
      )}
      {showUploadVideoButton && (
        <SummarizeAudioVideo
          disclosure={uploadVideoDisclosure}
          meeting={meeting}
        />
      )}
    </Stack>
  );
}

export default memo(MeetingPageRightRail);
